/**
 * Customer adapter
 * Converts Storefront API Customer to Analyzify format
 */

import type { StorefrontCustomer } from '../types/storefront';
import type { AnalyzifyCustomer, AnalyzifyCustomerMember, AnalyzifyCustomerVisitor } from '../types/products';
import { extractNumericId } from './ids';

/**
 * Hash a string with SHA-256
 * Used for GA4 Enhanced Conversions and privacy-safe tracking
 * @param value - The string to hash
 * @param normalize - Whether to lowercase and trim (default: true for email, phone)
 */
async function sha256Hash(value: string, normalize: boolean = true): Promise<string> {
  if (typeof window === 'undefined' || !window.crypto?.subtle) {
    // Fallback for non-browser environments - return empty to indicate hashing failed
    return '';
  }

  const normalizedValue = normalize ? value.toLowerCase().trim() : value.trim();
  const encoder = new TextEncoder();
  const data = encoder.encode(normalizedValue);
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Normalize phone number for hashing
 * Removes spaces, dashes, parentheses, and leading zeros
 * Format: E.164 without + (e.g., "14155551234")
 */
function normalizePhone(phone: string): string {
  // Remove all non-digit characters
  return phone.replace(/\D/g, '');
}

/**
 * Adapt a Storefront API customer to Analyzify format
 *
 * @param customer - Storefront API customer object
 * @param options - Adapter options
 * @param options.includeRawPII - If true, includes raw PII fields (email, phone, names)
 */
export async function adaptCustomer(
  customer: StorefrontCustomer | null,
  options?: { includeRawPII?: boolean }
): Promise<AnalyzifyCustomer> {
  // No customer = visitor
  if (!customer) {
    return createVisitor();
  }

  const customerId = extractNumericId(customer.id);

  // Get last order name (e.g., "#1001") - not the timestamp
  let lastOrder: string | undefined;
  if (customer.orders.edges && customer.orders.edges.length > 0) {
    const orderNumber = customer.orders.edges[0].node.orderNumber;
    lastOrder = `#${orderNumber}`;
  }

  // Get total spent
  let totalSpent: number | undefined;
  if (customer.orders.edges && customer.orders.edges.length > 0) {
    totalSpent = customer.orders.edges.reduce((sum, edge) => {
      return sum + parseFloat(edge.node.totalPrice.amount);
    }, 0);
  }

  // Build SHA256 hashes for GA4 Enhanced Conversions
  const sha256Email = customer.email ? await sha256Hash(customer.email) : undefined;
  const sha256Phone = customer.phone ? await sha256Hash(normalizePhone(customer.phone)) : undefined;
  const sha256FirstName = customer.firstName ? await sha256Hash(customer.firstName) : undefined;
  const sha256LastName = customer.lastName ? await sha256Hash(customer.lastName) : undefined;

  // Respect the merchant's hide_raw_userdata privacy setting (mirrors
  // the Analyzify app configuration, which nulls raw PII when this is enabled).
  // The SHA256 hashes below are the privacy-preserving path and stay as-is.
  const hideRawPII =
    typeof window !== 'undefined' &&
    (window as any).analyzify?.hide_raw_userdata === true;

  const member: AnalyzifyCustomerMember = {
    type: 'member',
    user_id: customerId,
    id: customerId,
    first_name: hideRawPII ? undefined : customer.firstName || undefined,
    last_name: hideRawPII ? undefined : customer.lastName || undefined,
    b2b: false, // Storefront API doesn't expose B2B info directly
    last_order: lastOrder,
    orders_count: customer.orders.totalCount,
    total_spent: totalSpent,
    // Raw PII fields (included if option set - needed for some platforms);
    // suppressed entirely when merchant enables hide_raw_userdata.
    email_address:
      options?.includeRawPII && !hideRawPII ? customer.email?.toLowerCase().trim() : undefined,
    phone_number: hideRawPII ? undefined : customer.phone || undefined,
    // SHA256 hashed fields for GA4 Enhanced Conversions
    sha256_email_address: sha256Email || undefined,
    sha256_phone_number: sha256Phone || undefined,
    sha256_first_name: sha256FirstName || undefined,
    sha256_last_name: sha256LastName || undefined,
    marketing: customer.acceptsMarketing,
  };

  return member;
}

/**
 * Create a visitor customer object
 */
export function createVisitor(): AnalyzifyCustomerVisitor {
  return {
    type: 'visitor',
  };
}

/**
 * Synchronous version of customer adapter
 * Note: SHA256 hashing requires async, so SHA256 fields are not included in sync version
 * Use adaptCustomer() for full SHA256 support
 */
export function adaptCustomerSync(
  customer: StorefrontCustomer | null
): AnalyzifyCustomer {
  if (!customer) {
    return createVisitor();
  }

  const customerId = extractNumericId(customer.id);

  // Guard against missing orders (partial API data) — a public entry
  // (setPageContext) must never throw into the host app.
  const orderEdges = customer.orders?.edges;

  // Get last order name (e.g., "#1001")
  let lastOrder: string | undefined;
  if (orderEdges && orderEdges.length > 0) {
    const orderNumber = orderEdges[0].node.orderNumber;
    lastOrder = `#${orderNumber}`;
  }

  // Get total spent
  let totalSpent: number | undefined;
  if (orderEdges && orderEdges.length > 0) {
    totalSpent = orderEdges.reduce((sum, edge) => {
      return sum + parseFloat(edge.node.totalPrice.amount);
    }, 0);
  }

  // Respect the merchant's hide_raw_userdata privacy setting (mirrors
  // the Analyzify app configuration, which nulls raw PII when this is enabled).
  const hideRawPII =
    typeof window !== 'undefined' &&
    (window as any).analyzify?.hide_raw_userdata === true;

  return {
    type: 'member',
    user_id: customerId,
    id: customerId,
    first_name: hideRawPII ? undefined : customer.firstName || undefined,
    last_name: hideRawPII ? undefined : customer.lastName || undefined,
    b2b: false, // Storefront API limitation
    last_order: lastOrder,
    orders_count: customer.orders?.totalCount ?? 0,
    total_spent: totalSpent,
    // Raw PII fields — suppressed when merchant enables hide_raw_userdata
    email_address: hideRawPII ? undefined : customer.email?.toLowerCase().trim(),
    phone_number: hideRawPII ? undefined : customer.phone || undefined,
    // SHA256 fields not available in sync version - use adaptCustomer() for full support
    marketing: customer.acceptsMarketing,
  };
}
